import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Paper,
  Typography,
  IconButton,
  useMediaQuery,
  Backdrop,
  Tooltip,
  CircularProgress
} from '@mui/material'
import {
  ArrowBack,
  Email,
  Business,
  Analytics,
  Info,
  Edit,
  Send,
  HelpOutline
} from '@mui/icons-material'
import Mailbox from '../../mailTemplate/src/components/atomic/pages/Mailbox'
import CampaignFirms from './CampaignFirms'
import CampaignStats from './CampaignStats'
import CampaignDetailInfo from './CampaignDetailInfo'
import CampaignSidebar from './CampaignSidebar'
import CampaignTour from './CampaignTour'
import McDataServices from '../../services/McDataServices'

import { useGlobalStoreData, useCampaignStatus } from '../../store/globalStore'
import { useSDSisVisible } from '../../store/searchDataStore'

const CampaignDetailHeader = ({ campaign, onBack, headerActions, onStartTour, activeTab }) => {
  const { t } = useTranslation()
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={onBack}
          sx={{
            backgroundColor: 'grey.100',
            '&:hover': { backgroundColor: 'grey.200' }
          }}
        >
          <ArrowBack />
        </IconButton>

        <Typography
          variant="h4"
          component="h1"
          sx={{
            flex: 1,
            fontWeight: 600,
            color: 'primary.main'
          }}
        >
          {campaign?.name}
        </Typography>

        <Tooltip title={t('mailTemplate.campaigns.tour.startTour')} arrow>
          <IconButton
            onClick={onStartTour}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main', backgroundColor: 'rgba(15, 198, 185, 0.08)' }
            }}
          >
            <HelpOutline />
          </IconButton>
        </Tooltip>

        {headerActions && activeTab === 0 && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            {headerActions.onEditFirms && (
              <Box
                data-tour="campaign-edit-firms"
                onClick={headerActions.onEditFirms}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2.5,
                  py: 1,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  color: 'text.secondary',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)', borderColor: 'text.disabled' }
                }}
              >
                <Edit sx={{ fontSize: 20 }} />
                {t('mailTemplate.campaigns.detail.editFirms')}
              </Box>
            )}
            {headerActions.onSendEmails && (
              <Tooltip
                title={headerActions.sendEmailsDisabled ? t('mailTemplate.campaigns.detail.sendEmailsDisabledTooltip') : ''}
                arrow
                placement="bottom"
              >
                <Box
                  data-tour="campaign-send-emails"
                  onClick={headerActions.sendEmailsDisabled ? undefined : headerActions.onSendEmails}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2.5,
                    py: 1,
                    borderRadius: 1.5,
                    cursor: headerActions.sendEmailsDisabled ? 'default' : 'pointer',
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    backgroundColor: headerActions.sendEmailsDisabled ? '#B0BEC5' : '#0FC6B9',
                    opacity: headerActions.sendEmailsDisabled ? 0.7 : 1,
                    '&:hover': headerActions.sendEmailsDisabled ? {} : { backgroundColor: '#0DAB9F' }
                  }}
                >
                  {headerActions.sendEmailsLoading && <CircularProgress size={16} sx={{ color: '#fff' }} />}
                  <Send sx={{ fontSize: 20 }} />
                  {t('mailTemplate.campaigns.detail.sendEmails')}
                </Box>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`campaign-tabpanel-${index}`}
    aria-labelledby={`campaign-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ display: 'flex', flexDirection: 'column' }}>{children}</Box>}
  </div>
)

const CampaignDetail = ({ campaign, onCampaignUpdate }) => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const languageGSD = useGlobalStoreData.language()

  const searchPanelVisible = useSDSisVisible()
  const globalStatus = useCampaignStatus(campaign?.id)

  const status = globalStatus || campaign?.status

  const getDefaultTabIndex = useCallback((currentStatus) => {
    if (currentStatus === 'FAILED') return 2
    if (currentStatus === 'MAILING') return 2

    return 0
  }, [])

  const [tabValue, setTabValue] = useState(() => getDefaultTabIndex(status))
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [backdropOpen, setBackdropOpen] = useState(false)
  const [headerActions, setHeaderActions] = useState(null)
  const [limits, setLimits] = useState(null)
  const [tourRun, setTourRun] = useState(false)
  const [tourStepIndex, setTourStepIndex] = useState(-1)
  const [mailStatus, setMailStatus] = useState({ canSend: null, canReceive: null, loading: true })

  const b2bAccountId = useMemo(() => searchParams.get('b2bAccountId'), [searchParams])

  const isBelowMdScreen = useMediaQuery(theme => theme.breakpoints.down('md'))

  const isFailed = status === 'FAILED'

  const tabs = useMemo(() => {
    return [
      {
        id: 'campaignFirms',
        label: t('mailTemplate.campaigns.detail.campaignFirmsTab'),
        icon: <Business />,
        component: <CampaignFirms campaign={campaign} onCampaignUpdate={onCampaignUpdate} onHeaderActions={setHeaderActions} onLimitsUpdate={setLimits} tourStepIndex={tourStepIndex} />,
        disabled: isFailed,
        tooltip: isFailed ? t('mailTemplate.campaigns.detail.campaignFirmsDisabledTooltip') : undefined
      },
      {
        id: 'mailbox',
        label: t('mailTemplate.campaigns.detail.mailTab'),
        icon: <Email />,
        component: <Mailbox />,
        disabled: false
      },
      {
        id: 'campaignDetails',
        label: t('mailTemplate.campaigns.detail.campaignDetailsTab'),
        icon: <Info />,
        component: <CampaignDetailInfo campaign={campaign} />,
        disabled: false
      },
      {
        id: 'stats',
        label: t('mailTemplate.campaigns.detail.statsTab'),
        icon: <Analytics />,
        component: <CampaignStats campaign={campaign} />,
        disabled: false
      }
    ]
  }, [campaign, onCampaignUpdate, t, isFailed, tourStepIndex])

  const handleTabChange = useCallback((newValue) => {
    if (!tabs[newValue]?.disabled) {
      setTabValue(newValue)
    }
  }, [tabs])

  const handleBack = useCallback(() => {
    const targetURL = `${process.env.PUBLIC_URL}/${languageGSD}/campaigns?b2bAccountId=${b2bAccountId}`
    navigate(targetURL)
  }, [languageGSD, b2bAccountId, navigate])

  const handleBackdropClick = useCallback(() => {
    setSidebarOpen(false)
    setBackdropOpen(false)
  }, [])

  useEffect(() => {
    const newTabIndex = getDefaultTabIndex(status)
    setTabValue(newTabIndex)
  }, [status, getDefaultTabIndex])

  useEffect(() => {
    if (backdropOpen && !sidebarOpen) {
      setBackdropOpen(false)
    }
  }, [backdropOpen, sidebarOpen])

  useEffect(() => {
    if (backdropOpen && !isBelowMdScreen) {
      setBackdropOpen(false)
    }

    if (sidebarOpen && !isBelowMdScreen) {
      setSidebarOpen(false)
    }
  }, [backdropOpen, isBelowMdScreen, sidebarOpen])

  useEffect(() => {
    if (b2bAccountId) {
      McDataServices.getUsersByB2bAccount([b2bAccountId])
        .then(b2bUsers => {
          const b2bUser = b2bUsers?.find(u => u.ownedB2bAccountId === b2bAccountId)
          if (b2bUser?.limit) {
            setLimits(b2bUser.limit)
          }
          setMailStatus({ canSend: b2bUser?.canSend ?? null, canReceive: b2bUser?.canReceive ?? null, loading: false })
        })
        .catch(err => {
          console.error('Failed to fetch B2B account data:', err)
          setMailStatus(prev => ({ ...prev, loading: false }))
        })
    }
  }, [b2bAccountId])

  useEffect(() => {
    if (headerActions && !searchPanelVisible && !localStorage.getItem('campaign-tour-completed')) {
      const timer = setTimeout(() => setTourRun(true), 500)
      return () => clearTimeout(timer)
    }
  }, [headerActions, searchPanelVisible])

  const handleTourFinish = useCallback(() => {
    setTourRun(false)
    localStorage.setItem('campaign-tour-completed', 'true')
  }, [])

  return (
    <Box data-tour="campaign-page" sx={{ p: 2 }}>
      <CampaignTour run={tourRun} onFinish={handleTourFinish} onStepChange={setTourStepIndex} />
      <CampaignDetailHeader
        campaign={campaign}
        onBack={handleBack}
        headerActions={headerActions}
        onStartTour={() => setTourRun(true)}
        activeTab={tabValue}
      />

      <Paper sx={{
        borderRadius: 3,
        overflow: 'clip',
        display: 'flex',
        minHeight: '600px'
      }}>
        <CampaignSidebar
          isBelowMdScreen={isBelowMdScreen}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          campaignDetails={campaign}
          activeTab={tabValue}
          onTabChange={handleTabChange}
          tabs={tabs}
          limits={limits}
          b2bAccountId={b2bAccountId}
          mailStatus={mailStatus}
        />

        <Backdrop
          open={backdropOpen}
          onClick={handleBackdropClick}
          className='z-10'
          sx={{
            position: 'absolute'
          }}
        />

        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'visible'
        }}>
          {tabs.map((tab, index) => (
            <TabPanel key={index} value={tabValue} index={index}>
              <Box sx={{ p: 3, minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column' }}>
                {tab.component}
              </Box>
            </TabPanel>
          ))}
        </Box>
      </Paper>
    </Box>
  )
}

export default CampaignDetail
